import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Check, KeyRound, LogIn, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';

const RESEND_COOLDOWN_SECONDS = 45;

const PROVIDER_LABEL = { google: 'Google', facebook: 'Facebook' };

function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [oauthProvider, setOauthProvider] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const submit = async ({ isResend = false } = {}) => {
    setError('');
    if (!email.trim()) { setError(t('authPages.enterEmail')); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.noPassword) {
          setOauthProvider(data.authProvider || 'social');
          setSent(false);
        } else {
          setSent(true);
          setOauthProvider(null);
          if (isResend) setCooldown(RESEND_COOLDOWN_SECONDS);
        }
      } else {
        setError(data.error || t('authPages.somethingWrong'));
      }
    } catch {
      setError(t('authPages.somethingWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); submit(); };
  const handleResend = () => submit({ isResend: true });

  const providerLabel = PROVIDER_LABEL[oauthProvider] || t('authPages.socialAccountGeneric');

  return (
    <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7f0', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 420, background: 'white', borderRadius: 16, padding: '32px 28px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <Link to="/registration" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#32614d', fontWeight: 600, fontSize: 13, textDecoration: 'none', marginBottom: 20 }}>
          <ArrowLeft size={15} /> {t('authPages.backToLogin')}
        </Link>

        {sent ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#e8f5e9,#d4ecd9)', color: '#32614d', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 4px 14px rgba(50,97,77,0.18)' }}>
              <Check size={26} strokeWidth={2.6} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3d2e', margin: '0 0 8px' }}>{t('authPages.checkYourEmail')}</h2>
            <p style={{ fontSize: 14, color: '#666', margin: '0 0 18px', lineHeight: 1.55 }}>
              {t('authPages.resetEmailSent', { email })}
            </p>
            <p style={{ fontSize: 12.5, color: '#9aa39c', margin: '0 0 20px', lineHeight: 1.5 }}>
              {t('authPages.checkSpamHint')}
            </p>
            <button type="button" onClick={handleResend} disabled={loading || cooldown > 0}
              style={{ background: 'none', border: 'none', padding: 0, fontSize: 13, fontWeight: 700, color: (loading || cooldown > 0) ? '#aaa' : '#32614d', cursor: (loading || cooldown > 0) ? 'not-allowed' : 'pointer' }}>
              {cooldown > 0 ? t('authPages.resendLinkIn', { seconds: cooldown }) : loading ? t('authPages.sending') : t('authPages.resendLink')}
            </button>
          </div>
        ) : oauthProvider ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#eaf6ee,#dcebe2)', color: '#32614d', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 4px 14px rgba(50,97,77,0.18)' }}>
              <LogIn size={24} strokeWidth={2.4} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a3d2e', margin: '0 0 8px' }}>
              {t('authPages.oauthNoticeTitle', { provider: providerLabel })}
            </h2>
            <p style={{ fontSize: 14, color: '#666', margin: '0 0 22px', lineHeight: 1.55 }}>
              {t('authPages.oauthNoticeDesc', { provider: providerLabel })}
            </p>
            <Link to="/registration" style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
              padding: 13, borderRadius: 10, background: 'linear-gradient(135deg,#32614d,#2a5240)', color: 'white',
              fontSize: 15, fontWeight: 700, textDecoration: 'none', boxSizing: 'border-box',
            }}>
              <LogIn size={16} strokeWidth={2.4} /> {t('authPages.goToLogin')}
            </Link>
          </div>
        ) : (
          <>
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: 'linear-gradient(135deg,#32614d,#2a5240)', color: '#e8f5e9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16, boxShadow: '0 6px 16px rgba(42,82,64,0.28)',
            }}>
              <KeyRound size={22} strokeWidth={2.2} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a3d2e', margin: '0 0 6px' }}>{t('authPages.forgotTitle')}</h2>
            <p style={{ fontSize: 14, color: '#666', margin: '0 0 24px' }}>{t('authPages.forgotSubtitle')}</p>
            <form onSubmit={handleSubmit}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>{t('authPages.email')}</label>
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <Mail size={16} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required autoFocus
                  style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                  onFocus={(e) => { e.target.style.borderColor = '#32614d'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#ddd'; }}
                />
              </div>
              {error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#ffebee', color: '#c62828', padding: '10px 14px', borderRadius: 10, fontSize: 12, border: '1px solid #ffcdd2', marginBottom: 14 }}>
                  <AlertCircle size={14} style={{ flexShrink: 0 }} /> {localizeMessage(error)}
                </div>
              )}
              <button type="submit" disabled={loading}
                style={{ width: '100%', padding: 13, borderRadius: 10, background: loading ? '#ccc' : 'linear-gradient(135deg,#32614d,#2a5240)', color: 'white', border: 'none', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? t('authPages.sending') : t('authPages.sendResetLink')}
              </button>
            </form>
          </>
        )}
      </div>
    </section>
  );
}

export default ForgotPassword;
